/**
 * Provides the `getRelativeTime` utility function for calculating and returning a localized, human-readable relative time string based on a provided date/time source or a relative offset in seconds, and its associated `GetRelativeTimeSourceType`, `GetRelativeTimeDateSource`, and `GetRelativeTimeSecondsSource` types.
 * @module getRelativeTime
 */
// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';
// Import i18n functions
import { __, _n, sprintf } from '@wordpress/i18n';
// Import dayjs and the plugins
import * as dayjsNamespace from 'dayjs';
import * as relativeTimePlugin from 'dayjs/plugin/relativeTime';
import * as updateLocalePlugin from 'dayjs/plugin/updateLocale';
// Resolve the actual dayjs object, handling potential .default from esModuleInterop
const dayjs = dayjsNamespace.default || dayjsNamespace;
// Extend dayjs with necessary plugins
dayjs.extend(updateLocalePlugin.default || updateLocalePlugin);
dayjs.extend(relativeTimePlugin.default || relativeTimePlugin);
// Configure dayjs locale for relative time.
dayjs.updateLocale('en', {
    relativeTime: {
        /* translators: %s: A relative time in the future, like "in 5 seconds" or "in 1 year" */
        future: escapeHTML(__('in %s', 'uncanny-automator')),
        /* translators: %s: A relative time in the past, like "5 seconds ago" or "1 year ago" */
        past: escapeHTML(__('%s ago', 'uncanny-automator')),
        m: escapeHTML(__('a minute', 'uncanny-automator')),
        /* translators: %d: Number of minutes */
        mm: escapeHTML(__('%d minutes', 'uncanny-automator')),
        h: escapeHTML(__('an hour', 'uncanny-automator')),
        /* translators: %d: Number of hours */
        hh: escapeHTML(__('%d hours', 'uncanny-automator')),
        d: escapeHTML(__('a day', 'uncanny-automator')),
        /* translators: %d: Number of days */
        dd: escapeHTML(__('%d days', 'uncanny-automator')),
        M: escapeHTML(__('a month', 'uncanny-automator')),
        /* translators: %d: Number of months */
        MM: escapeHTML(__('%d months', 'uncanny-automator')),
        y: escapeHTML(__('a year', 'uncanny-automator')),
        /* translators: %d: Number of years */
        yy: escapeHTML(__('%d years', 'uncanny-automator')),
        s: (value, withoutSuffix, key, isFuture) => {
            const numberValue = Number(value);
            if (isNaN(numberValue)) {
                // Fallback for safety, though dayjs should provide a number
                return escapeHTML(__('a few seconds', 'uncanny-automator'));
            }
            return escapeHTML(sprintf(_n('a second', '%d seconds', numberValue, 'uncanny-automator'), numberValue));
        },
    },
});
/**
 * Calculates and returns a localized, human-readable relative time string (e.g., "5 minutes ago", "in 2 hours") based on a provided date/time source or a relative offset in seconds.
 *
 * **Behavior:**
 * - Uses the `dayjs` library with `relativeTime` and `updateLocale` plugins enabled.
 * - Configures `dayjs` locale settings to use `@wordpress/i18n` functions (`__`, `_n`, `sprintf`) for translatable output strings (including singular/plural forms).
 * - Accepts two main modes via `sourceType`:
 * - `'date'`: The `source` should be a value interpretable by `dayjs` (Date object, timestamp number, ISO 8601 string). Calculates time relative to now.
 * - `'relative-seconds'`: The `source` must be a number representing seconds relative to now (positive for future, negative for past). Calculates the target date first, then the relative time.
 * - Validates inputs: throws errors for empty/null/undefined `source`, invalid `sourceType`, non-numeric `source` when `sourceType` is 'relative-seconds', or if `dayjs` cannot parse the input into a valid date.
 * - Returns the formatted relative time string generated by `dayjs().fromNow()`, incorporating the localized strings.
 *
 * @since 5.0 Initial implementation.
 * @param {GetRelativeTimeSourceType} sourceType - Specifies how to interpret the `source`: 'date' or 'relative-seconds'.
 * @param {GetRelativeTimeDateSource | GetRelativeTimeSecondsSource} source - The source value.
 * - If `sourceType` is 'date', this can be a `Date` object, a timestamp (`number`), or a date string parseable by `dayjs`.
 * - If `sourceType` is 'relative-seconds', this must be a `number` representing the offset in seconds from the current time.
 * @return {string} The localized relative time string (e.g., "5 minutes ago", "in 2 hours", "a day ago").
 * @throws {Error} If `sourceType` is invalid, `source` is empty/null/undefined, `source` type doesn't match `sourceType`, or if `dayjs` fails to create a valid date from the source.
 *
 * @example Importing the getRelativeTime utility
 * ```typescript
 * import { getRelativeTime } from '@automator/shared/utils/get-relative-time';
 * ```
 *
 * @example Relative time from a past date string
 * Calculates time elapsed since the given date.
 * ```typescript
 * // Assuming 'now' is 2025-05-06T10:00:00Z
 * const pastDate = '2025-05-06T08:00:00Z';
 * getRelativeTime( 'date', pastDate );
 * // => '2 hours ago'
 * ```
 *
 * @example Relative time from a future Date object
 * Calculates time until the given date.
 * ```typescript
 * // Assuming 'now' is 2025-05-06T10:00:00Z
 * const futureDate = new Date( '2025-05-07T10:00:00Z' );
 * getRelativeTime( 'date', futureDate );
 * // => 'in a day'
 * ```
 *
 * @example Relative time from future seconds offset
 * Calculates time for an event 300 seconds (5 minutes) from now.
 * ```typescript
 * getRelativeTime( 'relative-seconds', 300 );
 * // => 'in 5 minutes'
 * ```
 *
 * @example Relative time from past seconds offset
 * Calculates time for an event 3600 seconds (1 hour) ago.
 * ```typescript
 * getRelativeTime( 'relative-seconds', -3600 );
 * // => 'an hour ago'
 * ```
 *
 * @example Handling zero seconds offset
 * ```typescript
 * getRelativeTime( 'relative-seconds', 0 );
 * // => '0 seconds ago'
 * ```
 *
 * @example Handling invalid date source
 * Throws an error if dayjs cannot parse the date source.
 * ```typescript
 * try {
 *     getRelativeTime( 'date', 'not-a-valid-date' );
 * } catch ( error ) {
 *     console.error( error ); // Logs Error: getRelativeTime: Couldn't create a valid date...
 * }
 * ```
 *
 * @example Handling invalid source type for relative-seconds
 * Throws an error if source is not a number when type is 'relative-seconds'.
 * ```typescript
 * try {
 *     getRelativeTime( 'relative-seconds', '60' ); // Source is a string, not number
 * } catch ( error) {
 *     console.error( error ); // Logs Error: getRelativeTime: source must be a number...
 * }
 * ```
 *
 * @remarks
 * - The accuracy of "a month" or "a year" depends on `dayjs`'s internal thresholds for relative time.
 * - For comprehensive behavior details, refer to the unit tests.
 * - To learn more about the `dayjs` library, visit the [dayjs documentation](https://day.js.org/docs/en/parse/parse).
 */
export function getRelativeTime(sourceType, source) {
    if (!isDefined(source) || source === '') {
        throw new Error(`getRelativeTime: source can't be empty, null, or undefined.`);
    }
    let targetDate;
    switch (sourceType) {
        case 'date':
            // `source` could be string, number (timestamp), or Date object for dayjs
            targetDate = dayjs(source);
            break;
        case 'relative-seconds':
            // Ensure `source` is treated as a number for 'relative-seconds'
            if (typeof source !== 'number') {
                throw new Error(`getRelativeTime: source must be a number for sourceType 'relative-seconds'. Received: ${typeof source}`);
            }
            targetDate = dayjs().add(source, 'second');
            break;
        default:
            // This case should ideally not be reached if sourceType is correctly typed, but it's good just in case.
            // The type system should prevent invalid sourceType values.
            const exhaustiveCheck = sourceType;
            throw new Error(`getRelativeTime: Invalid sourceType '${exhaustiveCheck}'. It must be either "date" or "relative-seconds".`);
    }
    // Check if the constructed date is valid.
    if (!targetDate.isValid()) {
        throw new Error(`getRelativeTime: Couldn't create a valid date from source: ${String(source)} with sourceType: ${sourceType}`);
    }
    // Return the relative time (e.g., "in 5 minutes", "2 hours ago").
    return targetDate.fromNow();
}
;
